import { eventLabel } from "pages/EventsManagement/EventDetailsOverview"
import { colourName, gameTypeName, joinNames, logTime, plural } from "pages/EventsManagement/components/Tabs/Logistics/labels"

// The Estimates page, on one organizer (`?organizer=`) or global: the sheet's "Details" link opens it.
export const estimatesLink = organizerId => `/logistics/estimates${organizerId ? `?organizer=${organizerId}` : ""}`

// "28 Sep 2026, 03:00" in the warehouse's time zone, where the nightly check runs at 03:00.
export const momentLabel = date => logTime(date, "Europe/Bucharest")

export const triggerName = trigger => ({ schedule: "Scheduled", manual: "Manual" })[trigger] || "Unknown"

// "4 groups × 4", or "4 groups" when the group size is not part of the format.
const groupsLabel = ({ groups, size }) => size == null ? plural(groups, "group") : `${plural(groups, "group")} × ${size}`
// A key a relaxed estimate dropped reads "any …".
const named = (value, noun, name) => value == null ? `any ${noun}` : name(value)
const keyNames = { category: "category", gameType: "game type", size: "group size", competitionLevel: "level" }
const percent = value => `${Math.round(value * 100)}%`
// "Level 5", or "Roșu": a Tenis10 competition's level is its colour, its estimates learning apart (PRD 16 LR15b (d)).
const levelName = level => Number.isFinite(Number(level)) ? `Level ${level}` : colourName(level)

// What each kind of estimate shows (ticket 22, Q8): its format, its value, the rule it falls back to
// without 3 similar competitions, and the history its table lacks.
const kinds = {
  places: {
    name: "Places",
    title: groupsLabel,
    details: ({ gameType, category }) => [named(gameType, "game type", gameTypeName), named(category, "category", eventLabel)].join(" · "),
    value: value => value ? plural(value, "place") : "No additional phase",
    change: value => String(value || "none"),
    fallback: "fixed rule",
    history: "finished groups competitions",
    used: "estimates",
  },
  occupancy: {
    name: "Occupancy",
    title: ({ category, gameType, competitionLevel }) => [named(category, "category", eventLabel), named(gameType, "game type", gameTypeName),
      named(competitionLevel, "level", levelName)].join(" · "),
    details: () => null,
    value: percent,
    change: percent,
    fallback: "full capacity",
    history: "finished competitions",
    used: "occupancy",
  },
}
// "4 groups × 4" over "Singles · Silver"; "Silver · Singles · Level 5" alone for the occupancy.
export const formatTitle = (kind, format) => kinds[kind].title(format)
export const formatDetails = (kind, format) => kinds[kind].details(format)
// "8 places", "No additional phase", "71%".
export const valueLabel = (kind, value) => kinds[kind].value(value)

// "8 ×23 · 4 ×6 · none ×2": how often each result occurred.
export const distributionLabel = (distribution = []) => distribution.length
  ? distribution.map(({ value, competitions }) => `${value || "none"} ×${competitions}`).join(" · ") : "—"

// "Global · any category": the level a row's estimate comes from. A row always has some history, so
// its fallback reads "too little history" (PRD: „fără istoric, de verificat”).
export const sourceLabel = (kind, { level, dropped }) => ({ organizer: "Organizer", global: "Global",
  relaxed: `Global · any ${joinNames(dropped.map(key => keyNames[key] || key))}` })[level] || `Too little history · ${kinds[kind].fallback}`

// What the key the next level dropped held: "in Silver", "in doubles", "with groups of 5", "at level 6".
const heldBy = (key, format) => ({ category: `in ${eventLabel(format.category)}`, gameType: `in ${gameTypeName(format.gameType).toLowerCase()}`,
  size: `with groups of ${format.size}`, competitionLevel: `at level ${Number.isFinite(Number(format.competitionLevel)) ? format.competitionLevel
    : colourName(format.competitionLevel)}` })[key]

// Why a row relaxed: "only 2 similar in Silver (minimum 3)", "the organizer has only 1 similar (minimum 3)".
// `skipped` are the levels tried first, each with fewer than 3 similar competitions.
export const reasonLabel = ({ level, dropped, format, skipped = [] }) => {
  const own = skipped.find(step => step.level === "organizer")
  const global = skipped.filter(step => step.level !== "organizer").pop()
  const reasons = [own && `the organizer has only ${own.competitions} similar`, global && (level === "fixed"
    ? `only ${global.competitions} similar, even after relaxing` : `only ${global.competitions} similar ${heldBy(dropped.find(key => !global.dropped.includes(key)), format)}`)]
    .filter(Boolean)
  return reasons.length ? `${reasons.join("; ")} (minimum 3)` : null
}

// What a table without rows says: before the first calculation, or for a scope without history.
export const emptyLabel = (kind, { calculated, organizerId, organizerName }) => {
  if (!calculated) return "No estimates yet: the nightly check at 03:00 or Recalculate now calculates them."
  const { history, used } = kinds[kind]
  return organizerId ? `${organizerName || "This organizer"} has no ${history} in the last 24 months: its sheets use the global ${used}.`
    : `No ${history} in the last 24 months.`
}

// "Places · 4 groups × 4 · Singles · Gold: 4 → 8": an estimate a run changed; "—" where it did not exist.
export const changeLabel = ({ kind, format, before, after }) => {
  const { name, title, details, change } = kinds[kind]
  const value = estimate => estimate == null ? "—" : change(estimate)
  return `${[name, title(format), details(format)].filter(Boolean).join(" · ")}: ${value(before)} → ${value(after)}`
}
